import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Globe2, ArrowRightLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Phase F.5 Batch 21 — Localization admin page (currency + locale config). */
export default function Localization() {
  const [cfg, setCfg] = useState(null);
  const [convertFrom, setConvertFrom] = useState("USD");
  const [convertTo, setConvertTo] = useState("CAD");
  const [convertAmount, setConvertAmount] = useState(100);
  const [conversion, setConversion] = useState(null);

  const load = useCallback(async () => {
    try { setCfg((await axios.get(`${API}/i18n/config`)).data); }
    catch (e) { toast.error("Failed to load config"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const r = await axios.put(`${API}/i18n/config`, cfg);
      setCfg(r.data); toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const convert = async () => {
    try {
      const r = await axios.get(`${API}/i18n/convert`, { params: { from: convertFrom, to: convertTo, amount: convertAmount } });
      setConversion(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Convert failed"); }
  };

  const addCurrency = (code) => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    if (cfg.supported_currencies.includes(c)) return;
    setCfg({
      ...cfg,
      supported_currencies: [...cfg.supported_currencies, c],
      exchange_rates: { ...cfg.exchange_rates, [c]: cfg.exchange_rates[c] || 1.0 },
    });
  };
  const removeCurrency = (code) => {
    const sc = cfg.supported_currencies.filter((c) => c !== code);
    const rates = { ...cfg.exchange_rates };
    delete rates[code];
    setCfg({ ...cfg, supported_currencies: sc, exchange_rates: rates });
  };

  if (!cfg) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4 p-4" data-testid="i18n-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Globe2 className="h-6 w-6" /> Localization</h1>
        <p className="text-sm text-muted-foreground">Default currency + locale, supported list, and exchange rates.</p>
      </div>

      <Card>
        <CardHeader><CardTitle>Defaults</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <div><Label>Default currency</Label><Input value={cfg.default_currency} onChange={(e) => setCfg({ ...cfg, default_currency: e.target.value })} data-testid="i18n-default-currency" /></div>
            <div><Label>Default locale</Label><Input value={cfg.default_locale} onChange={(e) => setCfg({ ...cfg, default_locale: e.target.value })} data-testid="i18n-default-locale" /></div>
            <div className="flex items-end">
              <label className="text-sm flex items-center gap-2"><Switch checked={!!cfg.show_currency_picker} onCheckedChange={(v) => setCfg({ ...cfg, show_currency_picker: v })} data-testid="i18n-show-picker" /> Show currency picker in app</label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Currencies & rates</CardTitle>
          <CardDescription>1 unit of default = rate × target. Rate of default itself must be 1.0.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-1" data-testid="i18n-currency-list">
            {cfg.supported_currencies.map((c) => (
              <div key={c} className="flex items-center gap-2" data-testid={`i18n-cur-${c}`}>
                <span className="font-mono w-14">{c}</span>
                <Input
                  type="number" step="0.0001"
                  value={cfg.exchange_rates[c] || 0}
                  onChange={(e) => setCfg({ ...cfg, exchange_rates: { ...cfg.exchange_rates, [c]: parseFloat(e.target.value) || 0 } })}
                  className="w-40"
                  data-testid={`i18n-rate-${c}`}
                />
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => removeCurrency(c)} data-testid={`i18n-remove-${c}`}>Remove</Button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <Input placeholder="add currency code (e.g. NZD)" id="new-cur" onKeyDown={(e) => { if (e.key === "Enter") { addCurrency(e.target.value); e.target.value = ""; } }} data-testid="i18n-new-currency" />
            <Button variant="outline" onClick={() => { const el = document.getElementById("new-cur"); addCurrency(el.value); el.value = ""; }} data-testid="i18n-add-currency-btn">Add</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Locales</CardTitle></CardHeader>
        <CardContent>
          <Input
            value={cfg.supported_locales.join(", ")}
            onChange={(e) => setCfg({ ...cfg, supported_locales: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
            placeholder="e.g. en-US, fr-CA, es-MX"
            data-testid="i18n-locales"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><ArrowRightLeft className="h-4 w-4" /> Conversion tool</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-end gap-2 flex-wrap">
            <div><Label>Amount</Label><Input type="number" value={convertAmount} onChange={(e) => setConvertAmount(parseFloat(e.target.value) || 0)} className="w-32" data-testid="i18n-conv-amount" /></div>
            <div><Label>From</Label><Input value={convertFrom} onChange={(e) => setConvertFrom(e.target.value.toUpperCase())} className="w-24" data-testid="i18n-conv-from" /></div>
            <div><Label>To</Label><Input value={convertTo} onChange={(e) => setConvertTo(e.target.value.toUpperCase())} className="w-24" data-testid="i18n-conv-to" /></div>
            <Button onClick={convert} className="bg-emerald-600 hover:bg-emerald-700" data-testid="i18n-conv-btn">Convert</Button>
            {conversion && (
              <p className="text-sm" data-testid="i18n-conv-result">
                {conversion.amount} {conversion.from} = <strong>{conversion.converted.toFixed(2)} {conversion.to}</strong>
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="i18n-save-btn">Save changes</Button>
    </div>
  );
}
